import React from 'react';
import { cn } from '@/lib/utils';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'outlined' | 'elevated' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  hover?: boolean;
  interactive?: boolean;
  children: React.ReactNode;
}

const cardVariants = {
  default: [
    'bg-white border border-gray-200',
    'shadow-apple-sm',
  ],
  outlined: [
    'bg-white border-2 border-gray-300',
    'shadow-none',
  ],
  elevated: [
    'bg-white border border-gray-200',
    'shadow-apple-lg',
  ],
  filled: [
    'bg-gray-50 border border-gray-200',
    'shadow-apple-sm',
  ],
};

const cardPadding = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

const baseCardStyles = [
  'rounded-xl',
  'transition-all duration-200 ease-apple',
];

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      hover = false,
      interactive = false,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          baseCardStyles,
          cardVariants[variant],
          cardPadding[padding],
          hover && 'hover:shadow-apple-md hover:-translate-y-0.5',
          interactive && [
            'cursor-pointer',
            'hover:shadow-apple-md hover:-translate-y-0.5',
            'active:translate-y-0 active:shadow-apple-sm',
          ],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  avatar?: React.ReactNode;
  divider?: boolean;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  avatar,
  divider = false,
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        'flex items-start justify-between',
        divider && 'border-b border-gray-200 pb-4 mb-4',
        className
      )}
      {...props}
    >
      <div className="flex items-start space-x-3 flex-1 min-w-0">
        {/* Avatar */}
        {avatar && (
          <div className="flex-shrink-0">{avatar}</div>
        )}

        {/* Title and Subtitle */}
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900 font-apple truncate">
              {title}
            </h3>
          )}
          {subtitle && (
            <p className="text-sm text-gray-500 font-apple mt-1">
              {subtitle}
            </p>
          )}
          {children}
        </div>
      </div>

      {/* Action */}
      {action && (
        <div className="flex-shrink-0 ml-3">{action}</div>
      )}
    </div>
  );
};

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const CardBody: React.FC<CardBodyProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={cn('text-gray-700', className)} {...props}>
      {children}
    </div>
  );
};

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  divider?: boolean;
  justify?: 'start' | 'center' | 'end' | 'between';
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  divider = false,
  justify = 'end',
  className,
  ...props
}) => {
  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div
      className={cn(
        'flex items-center',
        justifyClasses[justify],
        divider && 'border-t border-gray-200 pt-4 mt-4',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export interface MenuItemCardProps {
  name: string;
  description?: string;
  price: number;
  originalPrice?: number;
  dietType: 'veg' | 'non-veg' | 'vegan' | 'contains-eggs' | 'jain';
  imageUrl?: string;
  isPopular?: boolean;
  isNew?: boolean;
  isAvailable?: boolean;
  spiceLevel?: 1 | 2 | 3 | 4 | 5;
  preparationTime?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  onClick?: () => void;
  className?: string;
}

export const MenuItemCard: React.FC<MenuItemCardProps> = ({
  name,
  description,
  price,
  originalPrice,
  dietType,
  imageUrl,
  isPopular = false,
  isNew = false,
  isAvailable = true,
  spiceLevel,
  preparationTime,
  onEdit,
  onDelete,
  onClick,
  className,
}) => {
  const dietColors = {
    veg: 'bg-diet-veg',
    'non-veg': 'bg-diet-non-veg',
    vegan: 'bg-diet-vegan',
    'contains-eggs': 'bg-diet-eggs',
    jain: 'bg-diet-jain',
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Card
      interactive={Boolean(onClick)}
      hover={Boolean(onClick)}
      padding="none"
      className={cn(
        'overflow-hidden',
        !isAvailable && 'opacity-60',
        className
      )}
      onClick={onClick}
    >
      {/* Image and Badges */}
      <div className="relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-48 object-cover"
          />
        ) : (
          <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
            <span className="text-gray-400 text-sm">No image</span>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {/* Diet Type Badge */}
          <div className={cn('w-4 h-4 rounded-full', dietColors[dietType])} />

          {/* Popular Badge */}
          {isPopular && (
            <span className="bg-accent text-white text-xs px-2 py-1 rounded-full font-medium">
              Popular
            </span>
          )}

          {/* New Badge */}
          {isNew && (
            <span className="bg-apple-green text-white text-xs px-2 py-1 rounded-full font-medium">
              New
            </span>
          )}

          {/* Unavailable Badge */}
          {!isAvailable && (
            <span className="bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              Unavailable
            </span>
          )}
        </div>

        {/* Spice Level */}
        {spiceLevel && (
          <div className="absolute top-2 right-2">
            <span className="bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full">
              {'🌶️'.repeat(spiceLevel)}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Name and Description */}
        <div className="mb-3">
          <h3 className="font-semibold text-gray-900 font-apple text-lg leading-tight mb-1">
            {name}
          </h3>
          {description && (
            <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">
              {description}
            </p>
          )}
        </div>

        {/* Price and Meta Info */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Price */}
            <span className="text-lg font-bold text-gray-900 font-apple">
              {formatCurrency(price)}
            </span>
            
            {/* Original Price (if discounted) */}
            {originalPrice && originalPrice > price && (
              <span className="text-sm text-gray-500 line-through">
                {formatCurrency(originalPrice)}
              </span>
            )}
          </div>

          {/* Preparation Time */}
          {preparationTime && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {preparationTime} min
            </span>
          )}
        </div>

        {/* Action Buttons */}
        {(onEdit || onDelete) && (
          <div className="flex justify-end space-x-2 mt-3 pt-3 border-t border-gray-100">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="text-primary hover:text-primary/80 text-sm font-medium"
              >
                Edit
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="text-apple-red hover:text-red-600 text-sm font-medium"
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export interface RestaurantCardProps {
  name: string;
  description?: string;
  imageUrl?: string;
  slug: string;
  isActive: boolean;
  menuItemsCount?: number;
  qrCodeCount?: number;
  totalViews?: number;
  onEdit?: () => void;
  onView?: () => void;
  onDelete?: () => void;
  className?: string;
}

export const RestaurantCard: React.FC<RestaurantCardProps> = ({
  name,
  description,
  imageUrl,
  slug,
  isActive,
  menuItemsCount = 0,
  qrCodeCount = 0,
  totalViews = 0,
  onEdit,
  onView,
  onDelete,
  className,
}) => {
  return (
    <Card
      interactive={Boolean(onView)}
      hover={Boolean(onView)}
      padding="none"
      className={cn('overflow-hidden', className)}
      onClick={onView}
    >
      {/* Header Image */}
      <div className="relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={name}
            className="w-full h-32 object-cover"
          />
        ) : (
          <div className="w-full h-32 bg-primary flex items-center justify-center">
            <span className="text-white text-xl font-bold">
              {name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <span
            className={cn(
              'text-xs px-2 py-1 rounded-full font-medium',
              isActive
                ? 'bg-apple-green text-white'
                : 'bg-gray-500 text-white'
            )}
          >
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="mb-4">
          <h3 className="font-semibold text-gray-900 font-apple text-lg mb-1">
            {name}
          </h3>
          <p className="text-sm text-gray-500 font-mono">/{slug}</p>
          {description && (
            <p className="text-gray-600 text-sm mt-2 line-clamp-2">
              {description}
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{menuItemsCount}</div>
            <div className="text-xs text-gray-500">Items</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{qrCodeCount}</div>
            <div className="text-xs text-gray-500">QR Codes</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{totalViews}</div>
            <div className="text-xs text-gray-500">Views</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="flex-1 bg-primary text-white text-sm py-2 px-3 rounded-lg hover:bg-primary/80 transition-colors"
            >
              Manage
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-apple-red hover:text-red-600 text-sm py-2 px-3 border border-apple-red rounded-lg hover:bg-red-50 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </Card>
  );
};

export default Card;
